import { OrderSheet } from '@/src/types';
import React, { useMemo } from 'react';
import {
    getOrderItemAggregationKey,
    getOrderItemDisplayName,
} from '../../../utils/orderItemDisplay';

export const FieldChecklistView = ({ data, customerAliases, orderSheets, sortBy }: { data: any[], customerAliases?: Record<string, string>, orderSheets?: OrderSheet[], sortBy?: 'default' | 'name' }) => {
    const aggregatedStores = useMemo(() => {
        if (!data) return [];
        return data.map(store => {
            const map: Record<string, any> = {};
            (store.orders || [])
                .filter((order: any) => order.status !== 'CANCELLED')
                .forEach((order: any) => {
                    const phoneVal = (order.customerPhone || '').replace(/\D/g, '');
                    const sheetId = order.orderSheetId || order.storeId || store.id || '';
                    const name = (order.customerName || '').trim();
                    const key = `${sheetId}-${name}-${phoneVal}`;
                    if (!map[key]) {
                        map[key] = {
                            ...order,
                            items: [...(order.items || [])],
                        };
                    } else {
                        map[key].items = [...map[key].items, ...(order.items || [])];
                        const sellerMemo = order.sellerMemo?.trim();
                        if (
                            sellerMemo &&
                            !String(map[key].sellerMemo || '').includes(sellerMemo)
                        ) {
                            map[key].sellerMemo = map[key].sellerMemo
                                ? `${map[key].sellerMemo}\n${sellerMemo}`
                                : sellerMemo;
                        }
                    }
                });

            let aggregatedOrders = Object.values(map).map((order: any) => {
                const itemMap: Record<string, any> = {};
                order.items.forEach((item: any) => {
                    if (item.quantity <= 0) return;
                    const itemKey = getOrderItemAggregationKey(item, '알수없음');
                    if (!itemMap[itemKey]) {
                        itemMap[itemKey] = { ...item };
                    } else {
                        itemMap[itemKey].quantity += item.quantity;
                    }
                });
                const consolidatedItems = Object.values(itemMap);
                const totalPrice = consolidatedItems.reduce((sum: number, item: any) => sum + (item.price * item.quantity), 0);
                return { ...order, items: consolidatedItems, totalPrice };
            }).filter((order: any) => order.items.length > 0);

            let orders = aggregatedOrders;

            const getSortName = (order: any) => {
                const digits = (order.customerPhone || '').replace(/[^0-9]/g, '');
                const alias = customerAliases?.[digits];
                return alias ? `${alias} (${order.customerName})` : (order.customerName || '');
            };

            if (sortBy === 'name') {
                orders.sort((a, b) => getSortName(a).localeCompare(getSortName(b), 'ko-KR'));
            } else {
                // Default sort: by time descending
                orders.sort((a: any, b: any) => {
                    const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                    const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                    return timeB - timeA;
                });
            }

            const representativeOrderId = orders[0]?.orderSheetId;
            const orderSheet = orderSheets?.find(s => s.id === representativeOrderId);
            const deliveryDateStr = orderSheet?.deliveryDate ? new Date(orderSheet.deliveryDate).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short' }) : '미정';

            return {
                name: store.name,
                deliveryDateStr,
                orders
            };
        });
    }, [data, sortBy, customerAliases]);

    return (
        <div className="field-checklist-area text-black font-sans leading-relaxed">
            {aggregatedStores.map((store, idx) => (
                <div key={idx} className="page-break w-full mb-10 print:mb-0 print:p-8">
                    <div className="border-y-4 border-black py-4 mb-6">
                        <h2 className="text-xl font-black text-center mb-2">현장 배부표</h2>
                        <div className="flex justify-between items-center text-xs font-bold px-2">
                            <div>[배송지] : {store.name}</div>
                            <div>[배송일] : {store.deliveryDateStr}</div>
                        </div>
                    </div>

                    <table className="w-full border-collapse border border-black text-[9px] table-fixed">
                        <thead>
                            <tr className="bg-gray-100">
                                <th className="border border-black p-2 w-[5%]">연번</th>
                                <th className="border border-black p-2 text-left w-[52%]">주문 내역(상품명(수량))</th>
                                <th className="border border-black p-2 text-left w-[15%]">판매자 메모</th>
                                <th className="border border-black p-2 w-[15%]">고객명(전화번호 뒷자리)</th>
                                <th className="border border-black p-2 w-[13%] text-right">총금액</th>
                            </tr>
                        </thead>
                        <tbody>
                            {store.orders.map((order: any, oIdx: number) => {
                                const last4 = order.customerPhone ? order.customerPhone.slice(-4) : '****';
                                const itemsStr = (order.items || [])
                                    .filter((item: any) => item.quantity > 0)
                                    .map((item: any) => `${getOrderItemDisplayName(item, '알수없음')}(${item.quantity})`)
                                    .join(', ');
                                const displayName = customerAliases && order.customerPhone && customerAliases[order.customerPhone.replace(/[^0-9]/g, '')]
                                    ? customerAliases[order.customerPhone.replace(/[^0-9]/g, '')]
                                    : order.customerName;

                                return (
                                    <tr key={oIdx} className="h-10 checklist-row">
                                        <td className="border border-black px-2 pt-2 pb-3 text-[11px] text-center align-top leading-normal">
                                            <div className="mb-1">{oIdx + 1}</div>
                                        </td>
                                        <td className="border border-black px-2 pt-2 pb-3 text-left text-[11px] align-top leading-normal break-words">
                                            <div className="mb-1">{itemsStr}</div>
                                        </td>
                                        <td className="border border-black px-2 pt-2 pb-3 text-left text-[11px] align-top leading-normal whitespace-pre-wrap">
                                            <div className="mb-1">{order.sellerMemo?.trim() || ''}</div>
                                        </td>
                                        <td className="border border-black px-2 pt-2 pb-3 font-black text-[11px] align-top leading-normal whitespace-pre-wrap">
                                            <div className="mb-1">{displayName}({last4})</div>
                                        </td>
                                        <td className="border border-black px-2 pt-2 pb-3 text-right font-bold text-[11px] align-top leading-normal">
                                            <div className="mb-1">{order.totalPrice.toLocaleString()}</div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <div className="border-t-4 border-black mt-6"></div>
                </div>
            ))}
        </div>
    );
};
